<template>
  <div
    class="seat-map-grid"
    :style="{
      '--columns-count': maxSeatsInRow,
    }"
  >
    <SeatItem
      v-for="(seat, i) in row.seats"
      :key="i"
      :seat="seat"
      :seat-colors="getSeatColor(seat)"
      :is-selected="isSelected(seat)"
      :disabled="isDisabled(seat)"
      @click="updateSeat(seat)"
    />
  </div>
</template>

<script setup lang="ts">
  import type { PassengerKey } from '@aviata/booking-data';

  import SeatItem from './SeatItem.vue';
  import type { SerializedRow, SerializedSeat } from '../lib';
  import type { SeatMapLegend, PassengersState, SelectedSeat } from '../types';

  const props = defineProps<{
    row: SerializedRow;
    maxSeatsInRow: number;
    seatMapLegend: SeatMapLegend | null;
    seatMapState: PassengersState;
    passengersKeys: PassengerKey[];
    activePassengerKey: PassengerKey;
  }>();

  const emit = defineEmits<{
    onSelect: [SelectedSeat];
    onUnselect: [];
  }>();

  function getSeatColor(seat: SerializedSeat) {
    return props.seatMapLegend && props.seatMapLegend[seat.price?.amount || ''];
  }

  function updateSeat(seat: SerializedSeat) {
    if (isSelected(seat)) {
      unselectSeat();
    } else {
      selectSeat(seat);
    }
  }

  function selectSeat(seat: SerializedSeat) {
    const seatPrice = Number(seat.price?.amount) || 0;
    const originalSeatPrice = Number(seat.pricing?.total) || seatPrice;
    const selectedSeat = {
      coordinates: { ...seat.coordinates },
      seatPrice,
      originalSeatPrice,
      seatKey: seat.key,
    };
    emit('onSelect', selectedSeat);
  }

  function unselectSeat() {
    emit('onUnselect');
  }

  function isSelected(seat: SerializedSeat) {
    return props.passengersKeys
      .map(key => props.seatMapState[key])
      .some(state => {
        const { row, col } = state.coordinates;
        return row === seat.coordinates.row && col === seat.coordinates.col;
      });
  }

  // should disable seat if the seat selected by another passenger
  function isDisabled(seat: SerializedSeat) {
    const seatStateEntries = Object.entries(props.seatMapState).find(
      ([_, value]) => value.seatKey === seat.key,
    );
    if (seatStateEntries) {
      return (
        isSelected(seat) && seatStateEntries[0] !== props.activePassengerKey
      );
    }
  }
</script>

<style>
  @import '../assets/seat-map.css';
</style>
